/**
 * Shared profile-loading logic used by all auth provider implementations.
 *
 * Both `MsalAuthProvider` and `GoogleAuthProvider` call `fetchAndSetProfile`
 * after the user authenticates in order to hydrate the `user` / `profile`
 * state from the backend.
 */

import { useCallback, useState } from "react";
import { setTelemetryUser } from "@/config/telemetry";
import { ApiError } from "@/lib/api";
import { authService } from "@/lib/services";
import type { TeamMembership, User, UserPreferences, UserProfile } from "@/types";

export type PlatformBlockReason =
  | "PLATFORM_SUSPENDED"
  | "LICENSE_EXPIRED"
  | "SEAT_LIMIT_REACHED"
  | "USER_DEACTIVATED"
  | null;

export interface PlatformBlockedState {
  reason: PlatformBlockReason;
  message: string | null;
}

export function useProfileLoader() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [platformBlocked, setPlatformBlocked] = useState<PlatformBlockedState>({
    reason: null,
    message: null,
  });

  const fetchAndSetProfile = useCallback(async () => {
    try {
      const response = await authService.getProfile();

      // Clear any previous blocked state on successful profile load
      setPlatformBlocked({ reason: null, message: null });

      const backendUser = response.data.user as unknown as Record<string, unknown>;
      const transformedUser: User = {
        id: 0,
        uuid: backendUser.uuid as string,
        email: backendUser.email as string,
        name: backendUser.name as string,
        displayName: backendUser.name as string,
        role: (backendUser.role as string) || "user",
        avatarUrl: backendUser.avatarUrl as string | undefined,
        isPlatformAdmin: backendUser.role === "platform_admin" || backendUser.role === "install_admin",
        createdAt: backendUser.createdAt as string,
        updatedAt: (backendUser.updatedAt || backendUser.createdAt) as string,
      };

      const settings = (backendUser.settings || {}) as Record<string, unknown>;
      // Budget data from the backend
      const rawBudget = (response.data as unknown as Record<string, unknown>).budget as Record<string, unknown> | null;
      const budgetData = rawBudget
        ? {
            monthlyLimitUsd: rawBudget.monthlyLimitUsd as number,
            currentSpendUsd: rawBudget.currentSpendUsd as number,
            resetsAt: rawBudget.resetsAt as string,
            percentUsed: rawBudget.percentUsed as number,
          }
        : null;

      const transformedProfile: UserProfile = {
        user: transformedUser,
        teams: ((response.data.teams || []) as unknown as Record<string, unknown>[]).map((membership) => {
          const teamData = (membership.team as Record<string, unknown>) || {};
          return {
            team: {
              id: 0,
              uuid: (teamData.uuid as string) || "",
              name: (teamData.name as string) || "",
              description: teamData.description as string | undefined,
              isEveryoneTeam: (teamData.isEveryone as boolean) || false,
              createdAt: "",
              updatedAt: "",
              memberCount: (teamData.memberCount as number) || 0,
            },
            role: ((membership.role as string) || "member") as TeamMembership["role"],
            joinedAt: (membership.joinedAt as string) || "",
          };
        }),
        budget: budgetData,
        capabilities: {
          automatedSyncEnabled: Boolean(
            ((response.data as unknown as Record<string, unknown>).capabilities as Record<string, unknown> | undefined)
              ?.automatedSyncEnabled,
          ),
          oauthClientManagementEnabled: Boolean(
            ((response.data as unknown as Record<string, unknown>).capabilities as Record<string, unknown> | undefined)
              ?.oauthClientManagementEnabled,
          ),
        },
        preferences: {
          defaultModelId: (settings.defaultModelId || settings.default_model_id) as string | undefined,
          theme: ((settings.theme as string) || "dark") as UserPreferences["theme"],
          notificationsEnabled: (settings.notificationsEnabled ?? settings.notifications_enabled ?? true) as boolean,
          responseTone: (settings.responseTone || settings.response_tone) as UserPreferences["responseTone"],
          responseLength: (settings.responseLength || settings.response_length) as UserPreferences["responseLength"],
          customInstructions: (settings.customInstructions || settings.custom_instructions) as string | undefined,
          showContextInfo: (settings.showContextInfo ?? settings.show_context_info ?? false) as boolean,
          groupChatBench: (settings.groupChatBench || settings.group_chat_bench) as string[] | undefined,
        },
      };

      setProfile(transformedProfile);
      setUser(transformedUser);
      setTelemetryUser(transformedUser.uuid, transformedUser.email);
    } catch (err) {
      // Check for license/platform blocked errors
      if (err instanceof ApiError && err.status === 403 && err.data) {
        const data = err.data as { detail?: { code?: string; message?: string } };
        const code = data.detail?.code;
        if (
          code === "PLATFORM_SUSPENDED" ||
          code === "LICENSE_EXPIRED" ||
          code === "SEAT_LIMIT_REACHED" ||
          code === "USER_DEACTIVATED"
        ) {
          setPlatformBlocked({
            reason: code as PlatformBlockReason,
            message: data.detail?.message || null,
          });
          return; // Don't re-throw — the UI will show the blocked page
        }
      }
      // Re-throw for other errors (handled by caller)
      throw err;
    }
  }, []);

  const clearProfile = useCallback(() => {
    setUser(null);
    setProfile(null);
    setPlatformBlocked({ reason: null, message: null });
    setTelemetryUser(null);
  }, []);

  const updatePreferences = useCallback((prefs: Partial<UserProfile["preferences"]>) => {
    setProfile((prev) => {
      if (!prev) return prev;
      return { ...prev, preferences: { ...prev.preferences, ...prefs } };
    });
  }, []);

  return { user, profile, platformBlocked, fetchAndSetProfile, clearProfile, updatePreferences };
}
